import React from 'react';
import { Badge, Table } from '@parsina/framework';

import { useTranslation } from '../../i18n';
import { num } from '../../lib/aiPricingView';
import {
  Code, CountCell, ParsehCell, PriceSourceBadge, UsdCell, useEnumLabel,
} from './shared';

const STATUS_VARIANT = { ok: 'success', error: 'destructive', aborted: 'warning' };

/**
 * The forensic list (design §12.7): one row per invocation. Every identifier
 * — request id, owner id, service key — is an LTR island; every quantity is a
 * localised number; every NULL is «—».
 */
export default function LedgerTable({ rows, onOpen, empty, labelOf }) {
  const { t, formatDateTime, formatNumber } = useTranslation();
  const statusLabel = useEnumLabel('aiPricing.ledger.status');
  const ownerLabel = useEnumLabel('aiPricing.overview.owner');
  const reasonLabel = useEnumLabel('aiPricing.ledger.chargeReason');

  const columns = [
    { key: 'time', header: t('aiPricing.ledger.columns.time'), width: '11rem', render: (r) => <span className="tabular-nums">{formatDateTime(r.startedAt)}</span> },
    {
      key: 'service',
      header: t('aiPricing.ledger.columns.service'),
      render: (r) => (
        <div className="min-w-0">
          <div className="truncate font-semibold text-foreground">{labelOf(r.serviceKey)}</div>
          <div className="truncate text-xs text-muted-foreground"><Code>{r.serviceKey}</Code></div>
        </div>
      ),
    },
    {
      key: 'owner',
      header: t('aiPricing.ledger.columns.owner'),
      width: '11rem',
      render: (r) => (
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground">{ownerLabel(r.ownerType)}</div>
          <Code title={r.ownerId}>{String(r.ownerId || '').slice(0, 12)}</Code>
        </div>
      ),
    },
    { key: 'calls', header: t('aiPricing.ledger.columns.calls'), align: 'end', width: '5rem', render: (r) => <CountCell value={r.calls} /> },
    {
      key: 'tokens',
      header: t('aiPricing.ledger.columns.tokens'),
      align: 'end',
      width: '9rem',
      render: (r) => <span className="tabular-nums">{formatNumber(r.inputTokens ?? 0)} / {formatNumber(r.outputTokens ?? 0)}</span>,
    },
    { key: 'cost', header: t('aiPricing.ledger.columns.cost'), align: 'end', width: '8rem', render: (r) => <UsdCell value={r.costUsd} /> },
    {
      key: 'charged',
      header: t('aiPricing.ledger.columns.charged'),
      align: 'end',
      width: '8rem',
      render: (r) => (
        <span className="inline-flex flex-col items-end gap-0.5">
          <ParsehCell value={r.chargedParseh} />
          {num(r.chargedParseh) === 0 && r.chargeReason && r.chargeReason !== 'charged' ? (
            <Badge variant={r.chargeReason === 'wallet_error' ? 'destructive' : 'outline'}>{reasonLabel(r.chargeReason)}</Badge>
          ) : null}
        </span>
      ),
    },
    { key: 'source', header: t('aiPricing.ledger.columns.source'), width: '7rem', render: (r) => (r.priceSource ? <PriceSourceBadge source={r.priceSource} /> : t('common.value.none')) },
    { key: 'status', header: t('aiPricing.ledger.columns.status'), width: '6rem', render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'outline'}>{statusLabel(r.status)}</Badge> },
  ];

  return (
    <Table
      dense
      ariaLabel={t('aiPricing.ledger.tableAria')}
      columns={columns}
      rows={rows}
      rowKey={(r) => r.requestId}
      onRowActivate={onOpen}
      rowProps={(r) => ({ 'data-testid': `ai-ledger-row-${r.requestId}`, 'aria-label': t('aiPricing.ledger.open', { id: r.requestId }) })}
      empty={empty}
      data-testid="ai-ledger-table"
    />
  );
}
